import React, { useEffect, useState } from 'react';
import { getTodayDate } from '../utils/dateUtils';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import styled from 'styled-components';
import { Briefcase, RefreshCw, Send, AlertTriangle, ArrowDownToLine, ArrowUpFromLine, Building2, Layers3, X, Pencil, ShoppingCart } from 'lucide-react';

import { DateField } from './shared/DateField';

/**
 * Position-anchored commitment dashboard.
 *
 * <p>Lists active PE / alternative commitments for a chosen portfolio and
 * exposes the lifecycle actions that can only originate from the position
 * (per memory feedback_position_anchored_dispose):
 * <ul>
 *   <li>NAV Refresh → {@code POST /api/alternatives/commitments/{id}/nav-refresh}
 *       — emits an attrs-only MTM trade. Reported NAV is written to
 *       {@code fact_position_commitment_attrs.reported_nav} and synced to
 *       {@code fact_positions.market_value} by {@code CommitmentPostingSpi}.</li>
 *   <li>Dispose → {@code POST /api/alternatives/commitments/{id}/dispose} —
 *       emits a SELL/DISPOSE trade routed through the unified posting engine,
 *       propagating realized PnL to {@code fact_positions} on POST.</li>
 * </ul>
 *
 * <p>The acquisition (BUY) path lives in PrivateEquityTicketScreen; this
 * dashboard intentionally has no "Add Commitment" button.
 */

const Container = styled.div<{ $embedded?: boolean }>`
  padding: ${(p) => (p.$embedded ? '1rem' : '2rem')};
  background: transparent;
  color: #e2e8f0;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  flex-wrap: wrap;
  gap: 1rem;
  margin-bottom: 1.5rem;
`;

const PortfolioPickerWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  min-width: 280px;
`;

const HeaderActions = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 0.75rem;
  flex-wrap: wrap;
`;

const SecondaryNavBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.55rem 0.9rem;
  border-radius: 8px;
  font-size: 0.78rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  border: 1px solid rgba(139, 92, 246, 0.4);
  background: rgba(139, 92, 246, 0.12);
  color: #c4b5fd;
  white-space: nowrap;

  &:hover {
    border-color: rgba(139, 92, 246, 0.7);
    background: rgba(139, 92, 246, 0.22);
    color: #ede9fe;
  }
`;

const PortfolioLabel = styled.label`
  font-size: 0.7rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #64748b;
  font-weight: 700;
`;

const PortfolioSelect = styled.select`
  background: rgba(15, 23, 42, 0.7);
  border: 1px solid rgba(55, 65, 81, 0.5);
  border-radius: 8px;
  padding: 0.5rem 0.75rem;
  color: #f1f5f9;
  font-size: 0.875rem;
  &:focus { border-color: #3b82f6; outline: none; }
`;

const SummaryRow = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 1rem;
  margin-bottom: 1.5rem;
`;

const SummaryCard = styled.div`
  background: rgba(30, 41, 59, 0.5);
  border: 1px solid rgba(255, 255, 255, 0.05);
  border-radius: 12px;
  padding: 1rem 1.25rem;
`;

const SummaryLabel = styled.div`
  font-size: 0.7rem;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  font-weight: 700;
  margin-bottom: 0.4rem;
`;

const SummaryValue = styled.div<{ $tone?: 'pos' | 'neg' }>`
  font-size: 1.25rem;
  font-weight: 700;
  color: ${(p) => (p.$tone === 'pos' ? '#10b981' : p.$tone === 'neg' ? '#ef4444' : '#f1f5f9')};
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(360px, 1fr));
  gap: 1rem;
`;

const Card = styled.div<{ $closed?: boolean }>`
  background: rgba(30, 41, 59, 0.5);
  border: 1px solid ${(p) => (p.$closed ? 'rgba(239, 68, 68, 0.3)' : 'rgba(139, 92, 246, 0.2)')};
  border-radius: 12px;
  padding: 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  opacity: ${(p) => (p.$closed ? 0.7 : 1)};
`;

const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 0.5rem;
`;

const FundName = styled.div`
  font-size: 0.95rem;
  font-weight: 700;
  color: #f1f5f9;
  line-height: 1.3;
`;

const StatusPill = styled.span<{ $status: string }>`
  font-size: 0.65rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  padding: 0.2rem 0.5rem;
  border-radius: 999px;
  background: ${(p) =>
    p.$status === 'ACTIVE'
      ? 'rgba(16, 185, 129, 0.15)'
      : p.$status === 'CLOSED'
      ? 'rgba(239, 68, 68, 0.15)'
      : 'rgba(245, 158, 11, 0.15)'};
  color: ${(p) =>
    p.$status === 'ACTIVE' ? '#10b981' : p.$status === 'CLOSED' ? '#ef4444' : '#f59e0b'};
`;

const MetaRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.5rem 0.75rem;
  font-size: 0.78rem;
`;

const Meta = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.1rem;
`;

const MetaLabel = styled.span`
  color: #64748b;
  font-size: 0.65rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  font-weight: 700;
`;

const MetaValue = styled.span`
  color: #e2e8f0;
  font-weight: 600;
`;

const ProgressWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

const ProgressBar = styled.div`
  height: 6px;
  border-radius: 3px;
  background: rgba(15, 23, 42, 0.7);
  overflow: hidden;
`;

const ProgressFill = styled.div<{ $pct: number }>`
  height: 100%;
  width: ${(p) => Math.min(100, Math.max(0, p.$pct))}%;
  background: linear-gradient(90deg, #3b82f6, #8b5cf6);
  transition: width 0.3s ease;
`;

const ProgressLabel = styled.div`
  display: flex;
  justify-content: space-between;
  font-size: 0.7rem;
  color: #94a3b8;
`;

const Actions = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-top: 0.25rem;
`;

const ActionBtn = styled.button<{ $variant?: 'primary' | 'danger' | 'secondary' }>`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.35rem;
  padding: 0.5rem 0.75rem;
  border-radius: 6px;
  font-size: 0.75rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  border: 1px solid;
  background: ${(p) =>
    p.$variant === 'danger'
      ? 'rgba(239, 68, 68, 0.1)'
      : p.$variant === 'primary'
      ? 'rgba(59, 130, 246, 0.15)'
      : 'rgba(255, 255, 255, 0.05)'};
  border-color: ${(p) =>
    p.$variant === 'danger'
      ? 'rgba(239, 68, 68, 0.3)'
      : p.$variant === 'primary'
      ? 'rgba(59, 130, 246, 0.3)'
      : 'rgba(255, 255, 255, 0.1)'};
  color: ${(p) => (p.$variant === 'danger' ? '#fca5a5' : p.$variant === 'primary' ? '#93c5fd' : '#cbd5e1')};

  &:hover:not(:disabled) {
    filter: brightness(1.15);
  }
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  svg { width: 14px; height: 14px; }
`;

const Empty = styled.div`
  padding: 3rem 1.5rem;
  text-align: center;
  color: #64748b;
  background: rgba(30, 41, 59, 0.3);
  border: 1px dashed rgba(255, 255, 255, 0.08);
  border-radius: 12px;
`;

const Toast = styled.div<{ $tone: 'success' | 'error' }>`
  margin-bottom: 1rem;
  padding: 0.75rem 1rem;
  border-radius: 8px;
  font-size: 0.8rem;
  background: ${(p) => (p.$tone === 'success' ? 'rgba(16, 185, 129, 0.1)' : 'rgba(239, 68, 68, 0.1)')};
  border: 1px solid ${(p) => (p.$tone === 'success' ? 'rgba(16, 185, 129, 0.3)' : 'rgba(239, 68, 68, 0.3)')};
  color: ${(p) => (p.$tone === 'success' ? '#10b981' : '#fca5a5')};
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

interface Commitment {
  id: number;
  fundId?: number | null;
  portfolioId: number;
  securityId?: number | null;
  commitmentDate: string;
  commitmentAmount: number;
  commitmentCurrency?: string;
  calledAmount?: number;
  distributedAmount?: number;
  recallableAmount?: number;
  status: string;
  managementFeeRate?: number;
  carriedInterestRate?: number;
  preferredReturnRate?: number;
  sideLetterNotes?: string;
  // Phase 3 — waterfall type override + clawback (European wind-down)
  waterfallType?: 'AMERICAN' | 'EUROPEAN' | null;
  clawbackAmount?: number | null;
  clawbackPaid?: number | null;
  clawbackComputedAt?: string | null;
}

const WaterfallPill = styled.span<{ $type: 'AMERICAN' | 'EUROPEAN' }>`
  font-size: 0.62rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  padding: 0.18rem 0.45rem;
  border-radius: 4px;
  background: ${(p) => (p.$type === 'EUROPEAN' ? 'rgba(99, 102, 241, 0.15)' : 'rgba(148, 163, 184, 0.12)')};
  color: ${(p) => (p.$type === 'EUROPEAN' ? '#a5b4fc' : '#94a3b8')};
  border: 1px solid ${(p) => (p.$type === 'EUROPEAN' ? 'rgba(99, 102, 241, 0.3)' : 'rgba(148, 163, 184, 0.2)')};
`;

const ClawbackBanner = styled.div<{ $settled: boolean }>`
  margin-top: 0.25rem;
  padding: 0.35rem 0.55rem;
  border-radius: 6px;
  font-size: 0.7rem;
  background: ${(p) => (p.$settled ? 'rgba(16, 185, 129, 0.08)' : 'rgba(245, 158, 11, 0.1)')};
  border: 1px solid ${(p) => (p.$settled ? 'rgba(16, 185, 129, 0.25)' : 'rgba(245, 158, 11, 0.3)')};
  color: ${(p) => (p.$settled ? '#6ee7b7' : '#fcd34d')};
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 0.5rem;
`;

// ---------------- Modal primitives (institutional dark-theme) ----------------

const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(2, 6, 23, 0.78);
  backdrop-filter: blur(2px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 1rem;
`;

const ModalBox = styled.div`
  width: 100%;
  max-width: 520px;
  background: #0f172a;
  border: 1px solid rgba(99, 102, 241, 0.25);
  border-radius: 12px;
  box-shadow: 0 24px 60px -10px rgba(0, 0, 0, 0.6);
  display: flex;
  flex-direction: column;
  max-height: 92vh;
  overflow: hidden;
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 1rem 1.25rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
  gap: 1rem;
`;

const ModalTitle = styled.div`
  font-size: 0.95rem;
  font-weight: 700;
  color: #f1f5f9;
  letter-spacing: 0.01em;
`;

const ModalSubtitle = styled.div`
  font-size: 0.72rem;
  color: #94a3b8;
  margin-top: 0.2rem;
  font-weight: 500;
`;

const ModalClose = styled.button`
  background: transparent;
  border: none;
  color: #64748b;
  cursor: pointer;
  padding: 0.2rem;
  border-radius: 4px;
  display: flex;
  &:hover { color: #e2e8f0; background: rgba(255, 255, 255, 0.05); }
`;

const ModalBody = styled.div`
  padding: 1.25rem;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 0.85rem;
`;

const ModalFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
  padding: 0.85rem 1.25rem;
  border-top: 1px solid rgba(255, 255, 255, 0.06);
  background: rgba(15, 23, 42, 0.6);
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
`;

const FormLabel = styled.label`
  font-size: 0.65rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #94a3b8;
  font-weight: 700;
`;

const FormHint = styled.span`
  font-size: 0.7rem;
  color: #64748b;
  font-weight: 400;
  text-transform: none;
  letter-spacing: 0;
  margin-top: 0.15rem;
`;

const FormInput = styled.input`
  background: rgba(15, 23, 42, 0.7);
  border: 1px solid rgba(55, 65, 81, 0.6);
  border-radius: 6px;
  padding: 0.5rem 0.65rem;
  color: #f1f5f9;
  font-size: 0.85rem;
  font-family: inherit;
  &:focus { border-color: #6366f1; outline: none; }
`;

const FormSelect = styled.select`
  background: rgba(15, 23, 42, 0.7);
  border: 1px solid rgba(55, 65, 81, 0.6);
  border-radius: 6px;
  padding: 0.5rem 0.65rem;
  color: #f1f5f9;
  font-size: 0.85rem;
  font-family: inherit;
  &:focus { border-color: #6366f1; outline: none; }
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.85rem;
`;

const ModalBtn = styled.button<{ $variant?: 'primary' | 'ghost' }>`
  padding: 0.5rem 1rem;
  border-radius: 6px;
  font-size: 0.78rem;
  font-weight: 600;
  cursor: pointer;
  border: 1px solid;
  background: ${(p) =>
    p.$variant === 'primary' ? 'rgba(99, 102, 241, 0.2)' : 'transparent'};
  border-color: ${(p) =>
    p.$variant === 'primary' ? 'rgba(99, 102, 241, 0.45)' : 'rgba(255, 255, 255, 0.12)'};
  color: ${(p) => (p.$variant === 'primary' ? '#c7d2fe' : '#94a3b8')};
  &:hover:not(:disabled) { filter: brightness(1.15); }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

const RadioCard = styled.label<{ $selected: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  padding: 0.7rem 0.85rem;
  border-radius: 8px;
  border: 1px solid ${(p) => (p.$selected ? 'rgba(99, 102, 241, 0.55)' : 'rgba(55, 65, 81, 0.5)')};
  background: ${(p) => (p.$selected ? 'rgba(99, 102, 241, 0.12)' : 'rgba(15, 23, 42, 0.5)')};
  cursor: pointer;
  transition: all 0.12s;
  &:hover { border-color: rgba(99, 102, 241, 0.45); }
  input { display: none; }
`;

const RadioTitle = styled.span`
  font-size: 0.78rem;
  font-weight: 700;
  color: #f1f5f9;
`;

const RadioDesc = styled.span`
  font-size: 0.7rem;
  color: #94a3b8;
  line-height: 1.35;
`;

const fmtMoney = (v: number | null | undefined, ccy = 'USD') => {
  if (v == null) return '—';
  const sign = ccy === 'USD' ? '$' : ccy + ' ';
  if (Math.abs(v) >= 1e9) return `${sign}${(v / 1e9).toFixed(2)}B`;
  if (Math.abs(v) >= 1e6) return `${sign}${(v / 1e6).toFixed(2)}M`;
  if (Math.abs(v) >= 1e3) return `${sign}${(v / 1e3).toFixed(0)}K`;
  return `${sign}${v.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
};

const parseFundName = (notes: string | undefined): string | null => {
  if (!notes) return null;
  const match = notes.match(/Fund:\s*([^|]+)/);
  return match ? match[1].trim() : null;
};

interface Props {
  embedded?: boolean;
}

const CommitmentDashboard: React.FC<Props> = ({ embedded = false }) => {
  const navigate = useNavigate();
  const [portfolios, setPortfolios] = useState<any[]>([]);
  const [selectedPortfolio, setSelectedPortfolio] = useState<number | null>(null);
  const [commitments, setCommitments] = useState<Commitment[]>([]);
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState<number | null>(null);
  const [toast, setToast] = useState<{ tone: 'success' | 'error'; msg: string } | null>(null);
  // Distribution modal state — replaces the legacy 3-step window.prompt() chain.
  const [distModalCommit, setDistModalCommit] = useState<Commitment | null>(null);
  const [distAmount, setDistAmount] = useState<string>('');
  const [distRecallable, setDistRecallable] = useState<string>('0');
  const [distDate, setDistDate] = useState<string>(() => getTodayDate());
  const [distType, setDistType] = useState<'REGULAR' | 'LIQUIDATION' | 'RECYCLING'>('REGULAR');
  // Waterfall edit modal — provides UI entry to flip a commitment to EUROPEAN
  // so the wind-down + clawback workflow can be exercised end-to-end.
  const [wfModalCommit, setWfModalCommit] = useState<Commitment | null>(null);
  const [wfChoice, setWfChoice] = useState<'AMERICAN' | 'EUROPEAN' | ''>('');
  // D2 (p1-consolidation-decisions.md): PM-initiate actions use proper modals,
  // never window.prompt free-text for money.
  const [actionModal, setActionModal] = useState<{ type: 'nav' | 'call' | 'dispose'; c: Commitment } | null>(null);
  const [actionAmount, setActionAmount] = useState('');
  const [actionDate, setActionDate] = useState<string>(() => getTodayDate());

  useEffect(() => {
    const extractContent = (data: any): any[] => {
      if (!data) return [];
      if (Array.isArray(data)) return data;
      if (Array.isArray(data.content)) return data.content;
      return [];
    };
    axios
      .get('/api/dimensions/portfolios')
      .then((r) => {
        const list = extractContent(r.data);
        setPortfolios(list);
        if (list.length > 0 && selectedPortfolio == null) {
          setSelectedPortfolio(list[0].id);
        }
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadCommitments = (portfolioId: number) => {
    setLoading(true);
    axios
      .get(`/api/alternatives/commitments/portfolio/${portfolioId}`)
      .then((r) => {
        const data = r.data;
        const list = Array.isArray(data) ? data : Array.isArray(data?.content) ? data.content : [];
        setCommitments(list);
      })
      .catch(() => setCommitments([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (selectedPortfolio != null) loadCommitments(selectedPortfolio);
  }, [selectedPortfolio]);

  const flashToast = (tone: 'success' | 'error', msg: string) => {
    setToast({ tone, msg });
    setTimeout(() => setToast(null), 5000);
  };

  const openAction = (type: 'nav' | 'call' | 'dispose', c: Commitment) => {
    setActionModal({ type, c });
    setActionAmount('');
    setActionDate(getTodayDate());
  };

  const submitAction = async () => {
    if (!actionModal) return;
    const { type, c } = actionModal;
    const ccy = c.commitmentCurrency || 'USD';
    const amountNum = parseFloat(actionAmount);
    if (isNaN(amountNum) || amountNum <= 0) {
      flashToast('error', 'Enter a valid positive amount');
      return;
    }
    setBusyId(c.id);
    try {
      if (type === 'nav') {
        const resp = await axios.post(`/api/alternatives/commitments/${c.id}/nav-refresh`, null, { params: { nav: amountNum } });
        flashToast('success', `MTM trade ${resp.data?.mtmTradeId} created (DRAFT). Advance through the trade blotter to post.`);
      } else if (type === 'call') {
        const resp = await axios.post(`/api/alternatives/commitments/${c.id}/capital-call`, null,
          { params: { amount: amountNum, callDate: actionDate, paymentDate: actionDate, currency: ccy } });
        flashToast('success', `Capital call ${resp.data?.capitalCallId} funded — ${fmtMoney(amountNum, ccy)} debited from portfolio, attrs trade emitted.`);
      } else {
        const resp = await axios.post(`/api/alternatives/commitments/${c.id}/dispose`, null, { params: { salePrice: amountNum } });
        flashToast('success', `DISPOSE trade ${resp.data?.disposeTradeId} created (DRAFT). Commitment closes when the trade posts.`);
      }
      setActionModal(null);
      if (selectedPortfolio != null) loadCommitments(selectedPortfolio);
    } catch (err: any) {
      flashToast('error', err?.response?.data?.message || err?.message || 'Action failed');
    } finally {
      setBusyId(null);
    }
  };

  const openDistributionModal = (c: Commitment) => {
    setDistModalCommit(c);
    setDistAmount('');
    setDistRecallable('0');
    setDistDate(getTodayDate());
    setDistType('REGULAR');
  };

  const submitDistribution = async () => {
    const c = distModalCommit;
    if (!c) return;
    const ccy = c.commitmentCurrency || 'USD';
    const amountNum = parseFloat(distAmount);
    if (isNaN(amountNum) || amountNum <= 0) {
      flashToast('error', 'Distribution amount must be > 0');
      return;
    }
    const recallableNum = distRecallable.trim() === '' ? 0 : parseFloat(distRecallable);
    if (isNaN(recallableNum) || recallableNum < 0) {
      flashToast('error', 'Recallable amount must be ≥ 0');
      return;
    }
    if (recallableNum > amountNum) {
      flashToast('error', 'Recallable cannot exceed gross distribution');
      return;
    }
    if (!distDate) {
      flashToast('error', 'Distribution date is required');
      return;
    }

    setBusyId(c.id);
    try {
      const resp = await axios.post(
        `/api/alternatives/commitments/${c.id}/distribution`,
        null,
        {
          params: {
            amount: amountNum,
            distributionDate: distDate,
            paymentDate: distDate,
            currency: ccy,
            recallableAmount: recallableNum,
            distributionType: distType,
          },
        }
      );
      const distId = resp.data?.distributionId;
      flashToast(
        'success',
        `Distribution ${distId} received — ${fmtMoney(amountNum, ccy)} credited` +
          (recallableNum > 0 ? ` (${fmtMoney(recallableNum, ccy)} recallable)` : '') +
          `, attrs trade emitted.`
      );
      setDistModalCommit(null);
      if (selectedPortfolio != null) loadCommitments(selectedPortfolio);
    } catch (err: any) {
      flashToast('error', err?.response?.data?.message || err?.message || 'Distribution failed');
    } finally {
      setBusyId(null);
    }
  };

  const openWaterfallModal = (c: Commitment) => {
    setWfModalCommit(c);
    setWfChoice(c.waterfallType ?? '');
  };

  const submitWaterfallType = async () => {
    const c = wfModalCommit;
    if (!c) return;
    setBusyId(c.id);
    try {
      await axios.put(`/api/alternatives/phase3/commitments/${c.id}/waterfall-type`, {
        waterfallType: wfChoice === '' ? null : wfChoice,
      });
      flashToast(
        'success',
        wfChoice
          ? `Waterfall set to ${wfChoice} for ${parseFundName(c.sideLetterNotes) || `Commitment #${c.id}`}.`
          : `Waterfall override cleared — commitment now inherits fund-level default.`
      );
      setWfModalCommit(null);
      if (selectedPortfolio != null) loadCommitments(selectedPortfolio);
    } catch (err: any) {
      flashToast('error', err?.response?.data?.message || err?.message || 'Update failed');
    } finally {
      setBusyId(null);
    }
  };

  // D2: clawback settlement is owned by the Fund Wind-Down workflow (single surface).

  const totals = commitments.reduce(
    (acc, c) => {
      acc.committed += c.commitmentAmount || 0;
      acc.called += c.calledAmount || 0;
      acc.distributed += c.distributedAmount || 0;
      acc.unfunded += (c.commitmentAmount || 0) - (c.calledAmount || 0);
      if (c.status === 'ACTIVE') acc.active += 1;
      if (c.status === 'CLOSED') acc.closed += 1;
      return acc;
    },
    { committed: 0, called: 0, distributed: 0, unfunded: 0, active: 0, closed: 0 }
  );

  return (
    <Container $embedded={embedded}>
      <Header>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <Briefcase size={18} color="#8b5cf6" />
            <span style={{ fontSize: '1.25rem', fontWeight: 700, color: '#f1f5f9' }}>
              PE Commitments
            </span>
          </div>
          <div style={{ fontSize: '0.8rem', color: '#94a3b8', marginTop: '0.25rem' }}>
            NAV refresh and dispose lifecycle events originate from the position card.
          </div>
        </div>
        <HeaderActions>
          <SecondaryNavBtn
            onClick={() => navigate('/alternatives/secondary-purchases')}
            title="Acquire an LP interest from another investor"
          >
            <ShoppingCart size={14} />
            Secondary Purchase
          </SecondaryNavBtn>
          <PortfolioPickerWrap>
            <PortfolioLabel>Portfolio</PortfolioLabel>
            <PortfolioSelect
              value={selectedPortfolio ?? ''}
              onChange={(e) => setSelectedPortfolio(e.target.value ? parseInt(e.target.value, 10) : null)}
            >
              <option value="">— Select Portfolio —</option>
              {portfolios.map((p: any) => (
                <option key={p.id} value={p.id}>
                  {p.portfolioName}
                </option>
              ))}
            </PortfolioSelect>
          </PortfolioPickerWrap>
        </HeaderActions>
      </Header>

      {toast && (
        <Toast $tone={toast.tone}>
          {toast.tone === 'error' ? <AlertTriangle size={16} /> : <Send size={16} />}
          {toast.msg}
        </Toast>
      )}

      <SummaryRow>
        <SummaryCard>
          <SummaryLabel>Total Commitments</SummaryLabel>
          <SummaryValue>{commitments.length}</SummaryValue>
        </SummaryCard>
        <SummaryCard>
          <SummaryLabel>Active / Closed</SummaryLabel>
          <SummaryValue>
            {totals.active} / {totals.closed}
          </SummaryValue>
        </SummaryCard>
        <SummaryCard>
          <SummaryLabel>Committed</SummaryLabel>
          <SummaryValue>{fmtMoney(totals.committed)}</SummaryValue>
        </SummaryCard>
        <SummaryCard>
          <SummaryLabel>Called</SummaryLabel>
          <SummaryValue>{fmtMoney(totals.called)}</SummaryValue>
        </SummaryCard>
        <SummaryCard>
          <SummaryLabel>Unfunded</SummaryLabel>
          <SummaryValue>{fmtMoney(totals.unfunded)}</SummaryValue>
        </SummaryCard>
        <SummaryCard>
          <SummaryLabel>Distributed</SummaryLabel>
          <SummaryValue>{fmtMoney(totals.distributed)}</SummaryValue>
        </SummaryCard>
      </SummaryRow>

      {loading ? (
        <Empty>Loading commitments…</Empty>
      ) : commitments.length === 0 ? (
        <Empty>
          {selectedPortfolio == null
            ? 'Select a portfolio to view commitments.'
            : 'No commitments yet for this portfolio. Create one from the Private Equity ticket.'}
        </Empty>
      ) : (
        <Grid>
          {commitments.map((c) => {
            const calledPct = c.commitmentAmount
              ? ((c.calledAmount || 0) / c.commitmentAmount) * 100
              : 0;
            const distPct = c.commitmentAmount
              ? ((c.distributedAmount || 0) / c.commitmentAmount) * 100
              : 0;
            const fundName = parseFundName(c.sideLetterNotes) || `Commitment #${c.id}`;
            const closed = c.status === 'CLOSED';

            return (
              <Card key={c.id} $closed={closed}>
                <CardHeader>
                  <FundName>{fundName}</FundName>
                  <div style={{ display: 'flex', gap: '0.35rem', alignItems: 'center', flexWrap: 'wrap' }}>
                    <WaterfallPill
                      $type={c.waterfallType ?? 'AMERICAN'}
                      onClick={() => openWaterfallModal(c)}
                      title={
                        c.waterfallType === 'EUROPEAN'
                          ? 'European waterfall — whole-fund LP-first; carry held until LP capital + pref returned. Click to change.'
                          : c.waterfallType === 'AMERICAN'
                          ? 'American waterfall — deal-by-deal carry once each distribution clears the hurdle. Click to change.'
                          : 'Waterfall not set — inherits fund-level default. Click to override.'
                      }
                      style={{ cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '0.25rem' }}
                    >
                      {c.waterfallType ?? 'WATERFALL'}
                      <Pencil size={9} style={{ opacity: 0.7 }} />
                    </WaterfallPill>
                    <StatusPill $status={c.status}>{c.status}</StatusPill>
                  </div>
                </CardHeader>
                {c.clawbackAmount != null && c.clawbackAmount > 0 && (() => {
                  const paid = c.clawbackPaid || 0;
                  const owed = c.clawbackAmount - paid;
                  const settled = owed <= 0;
                  return (
                    <ClawbackBanner $settled={settled}>
                      <span>
                        {settled
                          ? `Clawback settled • ${fmtMoney(c.clawbackAmount, c.commitmentCurrency)}`
                          : `Clawback outstanding • ${fmtMoney(owed, c.commitmentCurrency)} of ${fmtMoney(c.clawbackAmount, c.commitmentCurrency)}`}
                      </span>
                      <span style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                        {c.clawbackComputedAt && (
                          <span style={{ opacity: 0.7, fontSize: '0.62rem' }}>
                            {new Date(c.clawbackComputedAt).toLocaleDateString()}
                          </span>
                        )}
                        {!settled && c.fundId != null && (
                          <button
                            onClick={() => navigate(`/alternatives/funds/${c.fundId}/wind-down`)}
                            style={{
                              background: 'rgba(245, 158, 11, 0.2)',
                              border: '1px solid rgba(245, 158, 11, 0.5)',
                              color: '#fcd34d',
                              padding: '0.15rem 0.5rem',
                              borderRadius: 4,
                              fontSize: '0.62rem',
                              fontWeight: 700,
                              textTransform: 'uppercase',
                              letterSpacing: '0.05em',
                              cursor: 'pointer',
                            }}
                            title="D2: clawback settlement lives on the Fund Wind-Down workflow"
                          >
                            Settle in Wind-Down →
                          </button>
                        )}
                      </span>
                    </ClawbackBanner>
                  );
                })()}

                <MetaRow>
                  <Meta>
                    <MetaLabel>Commitment</MetaLabel>
                    <MetaValue>{fmtMoney(c.commitmentAmount, c.commitmentCurrency)}</MetaValue>
                  </Meta>
                  <Meta>
                    <MetaLabel>Date</MetaLabel>
                    <MetaValue>{c.commitmentDate}</MetaValue>
                  </Meta>
                  <Meta>
                    <MetaLabel>Mgmt Fee</MetaLabel>
                    <MetaValue>
                      {c.managementFeeRate != null
                        ? `${c.managementFeeRate.toFixed(2)}%`
                        : '—'}
                    </MetaValue>
                  </Meta>
                  <Meta>
                    <MetaLabel>Carry</MetaLabel>
                    <MetaValue>
                      {c.carriedInterestRate != null
                        ? `${c.carriedInterestRate.toFixed(2)}%`
                        : '—'}
                    </MetaValue>
                  </Meta>
                </MetaRow>

                <ProgressWrap>
                  <ProgressLabel>
                    <span>Called {calledPct.toFixed(0)}%</span>
                    <span>{fmtMoney(c.calledAmount, c.commitmentCurrency)}</span>
                  </ProgressLabel>
                  <ProgressBar>
                    <ProgressFill $pct={calledPct} />
                  </ProgressBar>
                  <ProgressLabel>
                    <span>Distributed {distPct.toFixed(0)}%</span>
                    <span>{fmtMoney(c.distributedAmount, c.commitmentCurrency)}</span>
                  </ProgressLabel>
                  <ProgressBar>
                    <ProgressFill $pct={distPct} />
                  </ProgressBar>
                </ProgressWrap>

                <Actions>
                  <ActionBtn
                    $variant="primary"
                    disabled={closed || busyId === c.id}
                    onClick={() => openAction('call', c)}
                    title="Fund a capital call — debits cash and emits attrs-only CAPITAL_CALL trade"
                  >
                    <ArrowUpFromLine />
                    Capital Call
                  </ActionBtn>
                  <ActionBtn
                    $variant="primary"
                    disabled={closed || busyId === c.id}
                    onClick={() => openDistributionModal(c)}
                    title="Receive a distribution — credits cash and emits attrs-only DISTRIBUTION trade"
                  >
                    <ArrowDownToLine />
                    Distribution
                  </ActionBtn>
                </Actions>
                <Actions>
                  <ActionBtn
                    $variant="secondary"
                    disabled={closed || busyId === c.id}
                    onClick={() => openAction('nav', c)}
                    title="Emit MTM trade with new reported NAV"
                  >
                    <RefreshCw />
                    {busyId === c.id ? 'Working…' : 'NAV Refresh'}
                  </ActionBtn>
                  <ActionBtn
                    $variant="danger"
                    disabled={closed || busyId === c.id}
                    onClick={() => openAction('dispose', c)}
                    title="Emit DISPOSE trade for secondary sale"
                  >
                    <Send />
                    Dispose
                  </ActionBtn>
                </Actions>
                {c.fundId != null && (
                  <Actions>
                    <ActionBtn
                      $variant="secondary"
                      onClick={() =>
                        navigate(
                          `/portfolio-management?tab=alternatives&sub=portfolio-companies&pcs=roster&fundId=${c.fundId}`,
                        )
                      }
                      title="Direct portfolio companies held by this fund"
                    >
                      <Building2 />
                      View portfolio companies
                    </ActionBtn>
                    <ActionBtn
                      $variant="secondary"
                      onClick={() =>
                        navigate(
                          `/portfolio-management?tab=alternatives&sub=portfolio-companies&pcs=fof&fundId=${c.fundId}`,
                        )
                      }
                      title="Pro-rata exposure through nested fund layers (FoF only)"
                    >
                      <Layers3 />
                      Look-through to portcos
                    </ActionBtn>
                    {c.waterfallType === 'EUROPEAN' && (
                      <ActionBtn
                        $variant="secondary"
                        onClick={() => navigate(`/alternatives/funds/${c.fundId}/wind-down`)}
                        title="Open the fund-level European clawback workflow"
                      >
                        <AlertTriangle />
                        Fund wind-down
                      </ActionBtn>
                    )}
                  </Actions>
                )}
              </Card>
            );
          })}
        </Grid>
      )}

      {actionModal && (() => {
        const { type, c } = actionModal;
        const ccy = c.commitmentCurrency || 'USD';
        const fundName = parseFundName(c.sideLetterNotes) || `Commitment #${c.id}`;
        const submitting = busyId === c.id;
        const titles = { nav: 'Refresh reported NAV', call: 'Issue capital call', dispose: 'Secondary sale (dispose)' } as const;
        const labels = { nav: `Latest reported NAV (${ccy})`, call: `Capital call amount (${ccy})`, dispose: `Secondary sale price (${ccy})` } as const;
        const hints = {
          nav: 'Emits an MTM trade that traverses pre-clear → execute → post and updates market value.',
          call: `Drives the full call lifecycle and debits portfolio cash. Unfunded: ${fmtMoney((c.commitmentAmount || 0) - (c.calledAmount || 0), ccy)}.`,
          dispose: 'Emits a SELL/DISPOSE trade; realized P&L flows to positions on POST and the commitment closes.',
        } as const;
        return (
          <ModalOverlay onClick={() => !submitting && setActionModal(null)}>
            <ModalBox onClick={(e) => e.stopPropagation()}>
              <ModalHeader>
                <div>
                  <ModalTitle>{titles[type]}</ModalTitle>
                  <ModalSubtitle>{fundName} • {ccy}</ModalSubtitle>
                </div>
                <ModalClose onClick={() => setActionModal(null)} aria-label="Close">
                  <X size={16} />
                </ModalClose>
              </ModalHeader>
              <ModalBody>
                <FormGroup>
                  <FormLabel>{labels[type]}</FormLabel>
                  <FormInput type="number" step="0.01" min="0" autoFocus value={actionAmount}
                    placeholder="0.00" onChange={(e) => setActionAmount(e.target.value)} />
                  <FormHint>{hints[type]}</FormHint>
                </FormGroup>
                {type === 'call' && (
                  <FormGroup>
                    <FormLabel>Call / payment date</FormLabel>
                    <DateField value={actionDate ?? ''} onChange={iso => setActionDate(iso)} />
                  </FormGroup>
                )}
              </ModalBody>
              <ModalFooter>
                <ActionBtn $variant="secondary" onClick={() => setActionModal(null)} disabled={submitting}>Cancel</ActionBtn>
                <ActionBtn $variant="primary" onClick={submitAction}
                  disabled={submitting || !actionAmount || parseFloat(actionAmount) <= 0}>
                  {submitting ? 'Working…' : 'Confirm'}
                </ActionBtn>
              </ModalFooter>
            </ModalBox>
          </ModalOverlay>
        );
      })()}

      {distModalCommit && (() => {
        const c = distModalCommit;
        const ccy = c.commitmentCurrency || 'USD';
        const fundName = parseFundName(c.sideLetterNotes) || `Commitment #${c.id}`;
        const amountNum = parseFloat(distAmount);
        const recallableNum = distRecallable.trim() === '' ? 0 : parseFloat(distRecallable);
        const submitting = busyId === c.id;
        const recallableInvalid =
          !isNaN(recallableNum) && !isNaN(amountNum) && recallableNum > amountNum;
        return (
          <ModalOverlay onClick={() => !submitting && setDistModalCommit(null)}>
            <ModalBox onClick={(e) => e.stopPropagation()}>
              <ModalHeader>
                <div>
                  <ModalTitle>Record distribution</ModalTitle>
                  <ModalSubtitle>{fundName} • {ccy}</ModalSubtitle>
                </div>
                <ModalClose onClick={() => setDistModalCommit(null)} aria-label="Close">
                  <X size={16} />
                </ModalClose>
              </ModalHeader>
              <ModalBody>
                <FormGroup>
                  <FormLabel>Gross distribution ({ccy})</FormLabel>
                  <FormInput
                    type="number"
                    step="0.01"
                    min="0"
                    autoFocus
                    value={distAmount}
                    placeholder="0.00"
                    onChange={(e) => setDistAmount(e.target.value)}
                  />
                  <FormHint>
                    Total cash received from the GP, gross of any withholding.
                  </FormHint>
                </FormGroup>
                <FormGrid>
                  <FormGroup>
                    <FormLabel>Distribution / payment date</FormLabel>
                    <DateField
                      value={distDate ?? ''}
                      onChange={iso => setDistDate(iso)}
                    />
                  </FormGroup>
                  <FormGroup>
                    <FormLabel>Distribution type</FormLabel>
                    <FormSelect
                      value={distType}
                      onChange={(e) => setDistType(e.target.value as any)}
                    >
                      <option value="REGULAR">Regular</option>
                      <option value="LIQUIDATION">Liquidation</option>
                      <option value="RECYCLING">Recycling</option>
                    </FormSelect>
                  </FormGroup>
                </FormGrid>
                <FormGroup>
                  <FormLabel>Recallable amount ({ccy})</FormLabel>
                  <FormInput
                    type="number"
                    step="0.01"
                    min="0"
                    value={distRecallable}
                    onChange={(e) => setDistRecallable(e.target.value)}
                  />
                  <FormHint>
                    Portion eligible for clawback during the recycling period (LPA-defined).
                    The recallable balance is what the Recall workflow draws against
                    when the GP needs follow-on capital. Default 0 if not recallable.
                  </FormHint>
                  {recallableInvalid && (
                    <FormHint style={{ color: '#fca5a5' }}>
                      Recallable cannot exceed gross distribution.
                    </FormHint>
                  )}
                </FormGroup>
              </ModalBody>
              <ModalFooter>
                <ModalBtn $variant="ghost" onClick={() => setDistModalCommit(null)} disabled={submitting}>
                  Cancel
                </ModalBtn>
                <ModalBtn
                  $variant="primary"
                  onClick={submitDistribution}
                  disabled={submitting || isNaN(amountNum) || amountNum <= 0 || recallableInvalid}
                >
                  {submitting ? 'Posting…' : 'Record distribution'}
                </ModalBtn>
              </ModalFooter>
            </ModalBox>
          </ModalOverlay>
        );
      })()}

      {wfModalCommit && (() => {
        const c = wfModalCommit;
        const fundName = parseFundName(c.sideLetterNotes) || `Commitment #${c.id}`;
        const submitting = busyId === c.id;
        const dirty = (c.waterfallType ?? '') !== wfChoice;
        return (
          <ModalOverlay onClick={() => !submitting && setWfModalCommit(null)}>
            <ModalBox onClick={(e) => e.stopPropagation()} style={{ maxWidth: 460 }}>
              <ModalHeader>
                <div>
                  <ModalTitle>Set waterfall structure</ModalTitle>
                  <ModalSubtitle>{fundName}</ModalSubtitle>
                </div>
                <ModalClose onClick={() => setWfModalCommit(null)} aria-label="Close">
                  <X size={16} />
                </ModalClose>
              </ModalHeader>
              <ModalBody>
                <FormHint style={{ fontSize: '0.72rem', color: '#94a3b8' }}>
                  Per-commitment override. Side letters occasionally tweak the
                  fund-wide structure for a specific LP — set that here. Leave
                  unset to inherit the fund-level default.
                </FormHint>
                <RadioCard $selected={wfChoice === 'AMERICAN'}>
                  <input
                    type="radio"
                    name="wf"
                    checked={wfChoice === 'AMERICAN'}
                    onChange={() => setWfChoice('AMERICAN')}
                  />
                  <RadioTitle>American (deal-by-deal)</RadioTitle>
                  <RadioDesc>
                    GP takes carry on each profitable deal as it exits, once that
                    deal clears the preferred return hurdle. No clawback at
                    fund-end. Common in venture and growth.
                  </RadioDesc>
                </RadioCard>
                <RadioCard $selected={wfChoice === 'EUROPEAN'}>
                  <input
                    type="radio"
                    name="wf"
                    checked={wfChoice === 'EUROPEAN'}
                    onChange={() => setWfChoice('EUROPEAN')}
                  />
                  <RadioTitle>European (whole-fund, LP-first)</RadioTitle>
                  <RadioDesc>
                    LP receives all contributed capital plus preferred return
                    before the GP earns any carry. Clawback may apply at
                    fund-end if the GP was overpaid. Standard in buyout / PE.
                  </RadioDesc>
                </RadioCard>
                <RadioCard $selected={wfChoice === ''}>
                  <input
                    type="radio"
                    name="wf"
                    checked={wfChoice === ''}
                    onChange={() => setWfChoice('')}
                  />
                  <RadioTitle>Inherit fund default</RadioTitle>
                  <RadioDesc>
                    No per-commitment override. The fund's configured waterfall
                    governs distributions and any clawback computation.
                  </RadioDesc>
                </RadioCard>
              </ModalBody>
              <ModalFooter>
                <ModalBtn $variant="ghost" onClick={() => setWfModalCommit(null)} disabled={submitting}>
                  Cancel
                </ModalBtn>
                <ModalBtn
                  $variant="primary"
                  onClick={submitWaterfallType}
                  disabled={submitting || !dirty}
                >
                  {submitting ? 'Saving…' : 'Save'}
                </ModalBtn>
              </ModalFooter>
            </ModalBox>
          </ModalOverlay>
        );
      })()}
    </Container>
  );
};

export default CommitmentDashboard;
